import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  LocalTaxi as TaxiIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Refresh as RefreshIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';

import { CALL_LOG_RETENTION_DAYS, formatCallDuration } from '../../services/telephonyService';

const formatDateTime = (date) => (date ? date.toLocaleString('de-DE') : '-');

const stateChipColor = (row) => {
  if (row.isMissed) return 'error';
  if (row.state === 'ringing') return 'warning';
  if (row.state === 'connected') return 'info';
  return 'default';
};

const numberText = (row) => {
  if (row.callerNumber) return row.callerNumber;
  return row.isWithheld ? 'Unterdrückt' : '-';
};

const KennzahlCard = ({ label, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5">{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

/**
 * Anrufliste (Berichte-Tab)
 *
 * Zeigt die Anrufsessions des gewählten Zeitraums: Anrufe der Zentrale (FRITZ!Box-Connector)
 * und Anrufe, die der KI-Telefonist entgegengenommen hat. Gefiltert wird in der ReportsPage,
 * damit der CSV-Export dieselben Zeilen bekommt.
 */
const CallLogTab = ({
  rows = [],
  summary,
  loading = false,
  error = null,
  sourceFilter = 'all',
  onSourceFilterChange,
  stateFilter = 'all',
  onStateFilterChange,
  search = '',
  onSearchChange,
  onReload
}) => {
  const navigate = useNavigate();
  const [detailRow, setDetailRow] = useState(null);

  const bookingRateText = useMemo(() => {
    if (!summary || !summary.total) return '-';
    return `${Math.round((summary.withRide / summary.total) * 100)} %`;
  }, [summary]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <KennzahlCard label="Anrufe" value={summary?.total ?? 0} hint={`max. ${CALL_LOG_RETENTION_DAYS} Tage Historie`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KennzahlCard label="Zentrale" value={summary?.central ?? 0} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KennzahlCard label="KI-Telefonist" value={summary?.ai ?? 0} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KennzahlCard
            label="Verpasst"
            value={summary?.missed ?? 0}
            hint={`Fahrt entstanden: ${summary?.withRide ?? 0} (${bookingRateText})`}
          />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Quelle</InputLabel>
          <Select
            label="Quelle"
            value={sourceFilter}
            onChange={(event) => onSourceFilterChange?.(event.target.value)}
          >
            <MenuItem value="all">Alle Anrufe</MenuItem>
            <MenuItem value="central">Zentrale</MenuItem>
            <MenuItem value="ai">KI-Telefonist</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={stateFilter}
            onChange={(event) => onStateFilterChange?.(event.target.value)}
          >
            <MenuItem value="all">Alle Status</MenuItem>
            <MenuItem value="missed">Verpasst</MenuItem>
            <MenuItem value="answered">Angenommen</MenuItem>
            <MenuItem value="with_ride">Mit Fahrt</MenuItem>
            <MenuItem value="without_ride">Ohne Fahrt</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Rufnummer oder Kunde"
          value={search}
          onChange={(event) => onSearchChange?.(event.target.value)}
          sx={{ minWidth: 260 }}
        />

        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={() => onReload?.()} disabled={loading}>
          Aktualisieren
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Zeitpunkt</TableCell>
                <TableCell>Quelle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rufnummer</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell align="right">Dauer</TableCell>
                <TableCell>Fahrt</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Keine Anrufe im gewählten Zeitraum. Anrufe werden {CALL_LOG_RETENTION_DAYS} Tage aufbewahrt.
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDateTime(row.startedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={row.isAiCall ? <SmartToyIcon /> : <PhoneInTalkIcon />}
                      color={row.isAiCall ? 'info' : 'default'}
                      variant={row.isAiCall ? 'filled' : 'outlined'}
                      label={row.sourceLabel}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={stateChipColor(row)} label={row.stateLabel} />
                  </TableCell>
                  <TableCell>{numberText(row)}</TableCell>
                  <TableCell>
                    {row.customerId ? (
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => navigate(`/kunden/${row.customerId}`)}
                      >
                        {row.customerName || 'Kunde öffnen'}
                      </Link>
                    ) : (
                      <Tooltip title={row.matchLabel || ''}>
                        <Typography variant="body2" color="text.secondary">
                          {row.customerName || (row.isWithheld ? '-' : 'Unbekannt')}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCallDuration(row.durationSeconds)}</TableCell>
                  <TableCell>
                    {row.rideId ? (
                      <Button
                        size="small"
                        startIcon={<TaxiIcon />}
                        onClick={() => navigate(`/fahrten/${row.rideId}`)}
                      >
                        Fahrt
                      </Button>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setDetailRow(row)}>Ansehen</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Anruf vom {formatDateTime(detailRow?.startedAt)}
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  icon={detailRow.isAiCall ? <SmartToyIcon /> : <PhoneInTalkIcon />}
                  color={detailRow.isAiCall ? 'info' : 'default'}
                  label={detailRow.sourceLabel}
                />
                <Chip size="small" color={stateChipColor(detailRow)} label={detailRow.stateLabel} />
                {detailRow.matchLabel && <Chip size="small" variant="outlined" label={detailRow.matchLabel} />}
              </Stack>

              <Typography variant="body2">
                <strong>Anrufer:</strong> {numberText(detailRow)}
                {detailRow.customerName ? ` (${detailRow.customerName})` : ''}
              </Typography>
              {detailRow.customerAddress && (
                <Typography variant="body2" color="text.secondary">
                  {detailRow.customerAddress}
                </Typography>
              )}
              {detailRow.calledNumber && (
                <Typography variant="body2">
                  <strong>Angerufen:</strong> {detailRow.calledNumber}
                  {detailRow.line ? ` (${detailRow.line})` : ''}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Angenommen:</strong> {formatDateTime(detailRow.connectedAt)} &nbsp;
                <strong>Ende:</strong> {formatDateTime(detailRow.endedAt)} &nbsp;
                <strong>Dauer:</strong> {formatCallDuration(detailRow.durationSeconds)}
              </Typography>

              {detailRow.summary && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">Zusammenfassung des KI-Gesprächs</Typography>
                  <Typography variant="body2">{detailRow.summary}</Typography>
                </>
              )}

              {detailRow.transcript && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">Transkript</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflowY: 'auto' }}>
                    {detailRow.transcript}
                  </Typography>
                </>
              )}

              {detailRow.conversationLink && (
                <Link href={detailRow.conversationLink} target="_blank" rel="noopener noreferrer">
                  Gespräch bei fonio öffnen <OpenInNewIcon fontSize="inherit" />
                </Link>
              )}

              <Typography variant="caption" color="text.secondary">
                Session-ID: {detailRow.id}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detailRow?.customerId && (
            <Button
              startIcon={<PersonIcon />}
              onClick={() => navigate(`/kunden/${detailRow.customerId}`)}
            >
              Kunde
            </Button>
          )}
          {detailRow?.rideId && (
            <Button
              startIcon={<TaxiIcon />}
              onClick={() => navigate(`/fahrten/${detailRow.rideId}`)}
            >
              Fahrt
            </Button>
          )}
          <Button onClick={() => setDetailRow(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallLogTab;
